/**
 * Trade-card field extractor for TradeGPT `[Stock Symbol: X]` LLM outputs.
 * Client-side port of extract_trade.py: decides what to render purely from text
 * structure. Pipeline: iterBlocks -> extractBlock -> renderCard (gate) ->
 * normalize, with segmentMessage() as the streaming glue. Spreads go through
 * isSpread()/renderSpreadCard(); under-specified blocks fall back to text.
 */

const HEADER
  = /\[Stock Symbol:\s*\**\s*([A-Za-z][A-Za-z0-9.-]{0,9})\s*\**\s*\]/gi;

// "- <label>: <value>", optional bold; {kw} injected into the label.
const LABEL_TMPL
  = "^[ \\t]*[-*][ \\t]*\\**[ \\t]*[^:\\n]*?{kw}[^:\\n]*?\\**[ \\t]*:[ \\t]*(?<val>.*?)[ \\t]*$";
// keyword must START the label (short/ambiguous keys: iv, bid, mid, delta, pop).
const LABEL_ANCHORED_TMPL
  = "^[ \\t]*[-*][ \\t]*\\**[ \\t]*{kw}[^:\\n]*?\\**[ \\t]*:[ \\t]*(?<val>.*?)[ \\t]*$";
// value carried on the next indented line when the label line ends at the colon.
const NEXTLINE = "^[ \\t]+[-*]?[ \\t]*\\**[ \\t]*(?<val>\\S[^\\n]*?)[ \\t]*$";
const NEXTLINE_RE = new RegExp(NEXTLINE, "gm");

function clean(v: string | undefined): string | null {
  if (v == null) return null;
  const out = v.replace(/\*\*/g, "").replace(/^[\s*–\-:\t]+|[\s*–\-:\t]+$/g, "");
  return out || null;
}

function grab(block: string, re: RegExp): string | null {
  const m = re.exec(block);
  if (!m) return null;
  const v = clean(m.groups?.val);
  if (v) return v;
  // value empty -> first indented continuation line right after the label
  const matchEnd = (m.index ?? 0) + m[0].length;
  NEXTLINE_RE.lastIndex = matchEnd;
  const n = NEXTLINE_RE.exec(block);
  if (n && (n.index ?? 0) <= matchEnd + 1) return clean(n.groups?.val);
  return null;
}

// canonical field -> ordered [keyword, anchored?] (first hit wins).
const FIELD_LABELS: Record<string, Array<[string, boolean]>> = {
  signal: [["\\bsignal\\b", false]],
  strategy: [["\\boption strategy\\b", false], ["\\bstrategy\\b", true]],
  strike: [["\\bstrikes?\\b", false]],
  call_put: [["\\bcall/put\\b", false], ["\\boption type\\b", false]],
  buy_sell: [["\\bbuy/sell\\b", false]],
  entry_ask: [
    ["\\bask\\b", false], ["entry", true], ["premium", true],
    ["mid", true], ["bid", true],
  ],
  expiry: [["\\bexpir\\w*\\b", false]],
  current_price: [["\\bcurrent\\b[^:\\n]*\\bprice\\b", false], ["spot", true]],
  max_loss: [
    // explicit label first so a "Max risk/gain" heading can't shadow a real
    // "- Max Loss:" bullet; a long's debit cost IS its max loss ("Total outlay")
    ["\\bmax(?:imum)?\\s+loss\\b", false],
    ["\\bmax(?:imum)?\\s+risk\\b", false],
    ["\\btotal\\s+(?:outlay|debit)\\b", false],
  ],
  breakeven: [["\\bbreak[\\s-]?even\\b", false]],
  max_gain: [["\\bmax(?:imum)?\\s+gain\\b", false]],
  iv: [["\\bimplied volatility\\b", false], ["iv\\b", true]],
  pop: [
    ["\\bprobability of profit\\b", false],
    ["\\bitm probability\\b", false], ["pop\\b", true],
  ],
  stop_loss: [["\\bstop[\\s-]?loss\\b", false]],
  take_profit: [["\\btake[\\s-]?profit\\b", false]],
  delta: [["delta", true]],
  rationale: [["\\brationale\\b", false], ["\\bthesis\\b", false]],
};

// Economics labels also arrive as "= formula" lines, so for these the separator
// accepts '=' alongside ':'. ':' templates run FIRST so a parenthetical '=' can't win.
const EQ_FIELDS = new Set(["max_loss", "max_gain", "breakeven"]);
const eqTmpl = (tmpl: string) => tmpl.replace(":[ \\t]*(?<val>", "[:=][ \\t]*(?<val>");

// Precompiled once — extractBlock runs per block per render.
const FIELD_LABEL_RES: Record<string, RegExp[]> = Object.fromEntries(
  Object.entries(FIELD_LABELS).map(([field, kws]) => {
    const tmpls = kws.map(([kw, anchored]) =>
      [kw, anchored ? LABEL_ANCHORED_TMPL : LABEL_TMPL] as const);
    const res = tmpls.map(([kw, t]) => new RegExp(t.replace("{kw}", kw), "im"));
    if (EQ_FIELDS.has(field)) {
      res.push(...tmpls.map(([kw, t]) => new RegExp(eqTmpl(t).replace("{kw}", kw), "im")));
    }
    return [field, res];
  }),
);

const LONGSHORT = /\b(long|short)\s+(call|put)\b/i;
const DIRECTION = /^[ \t]*[-*][^:\n]*\b(bullish|bearish|neutral)\b/im;
const CP_BULLET = /^[ \t]*[-*][^:\n]*\b(call|put)\b[ \t]*$/im;
// strike in a contract phrase: "Buy: SOFI $19.5 Put", "the $450 call"
const CONTRACT_STRIKE = /(?:US)?\$\s*([\d,]+(?:\.\d+)?)\s+(call|put)s?\b/i;

const STRAT_LINE = /^[ \t]*[-*][ \t]*\**[ \t]*option strategy\b[^:\n]*:(.*)$/im;
const MULTI
  = /\b(spread|condor|butterfly|calendar|strangle|straddle|collar|iron|vertical|diagonal|ratio)\b/i;
// (short)…(long) paired roles on one line — two-leg evidence
const SHORTLONG_PAIR = /\(short\)[^\n]*\(long\)|\(long\)[^\n]*\(short\)/i;
// leg markers: the role pair, OR bulleted/bold-heading `Leg N`
const TWOLEG
  = new RegExp(`${SHORTLONG_PAIR.source}|^[ \\t]*[-*]?[ \\t]*\\**[ \\t]*leg[ \\t]*\\d`, "im");

export const GATE = ["signal", "strike", "call_put", "buy_sell"] as const; // + ticker
export const EXPECTED = ["entry_ask", "expiry", "current_price", "max_loss", "breakeven"] as const;

export type TradeFields = Record<string, string>;

export function isSpread(block: string): boolean {
  const sm = STRAT_LINE.exec(block);
  if (sm && MULTI.test(sm[1])) return true;
  return TWOLEG.test(block);
}

export function extractBlock(ticker: string, block: string): TradeFields {
  const out: TradeFields = { ticker };
  for (const [field, res] of Object.entries(FIELD_LABEL_RES)) {
    for (const re of res) {
      const v = grab(block, re);
      if (v) {
        out[field] = v;
        break;
      }
    }
  }

  if (!("strike" in out)) {
    const m = CONTRACT_STRIKE.exec(block);
    if (m) {
      out.strike = `$${m[1]}`;
      if (!("call_put" in out)) out.call_put = cap(m[2]);
    }
  }

  const ls = LONGSHORT.exec(block);
  if (!("call_put" in out)) {
    if (ls) {
      out.call_put = cap(ls[2]);
    }
    else {
      const m = CP_BULLET.exec(block);
      if (m) out.call_put = cap(m[1]);
    }
  }
  if (!("buy_sell" in out) && ls) {
    out.buy_sell = ls[1].toLowerCase() === "long" ? "Buy" : "Sell";
  }
  if (!("signal" in out)) {
    if (ls) {
      out.signal = ls[2].toLowerCase() === "call" ? "Bullish" : "Bearish";
    }
    else {
      const d = DIRECTION.exec(block);
      if (d) out.signal = cap(d[1]);
    }
  }
  return out;
}

// A lone "Leg 1" marker under a single-leg strategy ("Long Call") is formatting,
// not a spread — without this it dies between both parsers and falls to text.
function isLoneLegFormatting(block: string): boolean {
  const sm = STRAT_LINE.exec(block);
  if (!sm || MULTI.test(sm[1])) return false;
  if (SHORTLONG_PAIR.test(block)) return false;
  return Array.from(block.matchAll(LEG_HEADER_G)).length < 2;
}

export function renderCard(ticker: string, block: string): TradeFields | null {
  if (isSpread(block) && !isLoneLegFormatting(block)) return null;
  const fields = extractBlock(ticker, block);
  if (!GATE.every(f => f in fields)) return null;
  if (EXPECTED.filter(f => f in fields).length < 3) return null;
  return fields;
}

// Value normalizers — fitted to observed value shapes (see field-audit-prod.md).
const PRICE = /(?:US)?\$\s*(-?)\s*([\d,]+(?:\.\d+)?)/g;
const MONTHS: Record<string, number> = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
  jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
};
// DD <sep> Mon <sep> YYYY  (sep = hyphen, U+2011, space, slash)
const DATE = /\b(\d{1,2})[-‑/ ]([A-Za-z]{3,9})[-‑/ ](\d{4})\b/;
// Mon D, YYYY — US prose style ("May 1, 2026")
const DATE_MDY = /\b([A-Za-z]{3,9})\s+(\d{1,2}),\s*(\d{4})\b/;

export type NormValue = number | string | null;

/** First dollar amount (sign preserved); two amounts => "lo–hi" string. */
export function parsePrice(raw: string | null): NormValue {
  if (raw == null) return null;
  const nums = Array.from(raw.matchAll(PRICE));
  if (!nums.length) return null;
  const f = (sign: string, n: string) =>
    parseFloat(n.replace(/,/g, "")) * (sign === "-" ? -1 : 1);
  const vals = nums.map(m => f(m[1], m[2]));
  return vals.length === 1 ? vals[0] : `${vals[0]}–${vals[1]}`;
}

// A $-figure beside an unresolved term ("US$15 – net_debit") is an unevaluated
// formula; reading it would overstate the card, so it parses to null.
const UNRESOLVED_TERM = /\bnet[\s_-]?(?:debit|credit|premium|cost)|\b(?:ask|bid)\s*\(/i;
// Illustrative lines carry placeholder figures, not real economics.
const EXAMPLE_LINE = /\b(?:e\.?g\.?|examples?|for\s+instance|using\s+that)\b/i;

/** Single spread-economics figure; reads the value after the last '='. */
export function parseEconAmount(raw: string | null): NormValue {
  if (raw == null) return null;
  const eq = raw.lastIndexOf("=");
  const one = (s: string): number | null => {
    if (UNRESOLVED_TERM.test(s)) return null;
    const m = /(?:US)?\$\s*(-?)\s*([\d,]+(?:\.\d+)?)/.exec(s);
    return m ? parseFloat(m[2].replace(/,/g, "")) * (m[1] === "-" ? -1 : 1) : null;
  };
  return (eq >= 0 ? one(raw.slice(eq + 1)) : null) ?? one(raw);
}

/** DD-Mon-YYYY or "Mon D, YYYY" -> ISO "YYYY-MM-DD". */
export function parseDate(raw: string | null): NormValue {
  if (raw == null) return null;
  let day: string, mon: string, year: string;
  const dmy = DATE.exec(raw);
  if (dmy) [day, mon, year] = [dmy[1], dmy[2], dmy[3]];
  else {
    const mdy = DATE_MDY.exec(raw);
    if (!mdy) return null;
    [mon, day, year] = [mdy[1], mdy[2], mdy[3]];
  }
  const key = mon.slice(0, 3).toLowerCase();
  if (!(key in MONTHS)) return null;
  return `${year}-${String(MONTHS[key]).padStart(2, "0")}-${String(+day).padStart(2, "0")}`;
}

const enumParser = (re: RegExp) => (raw: string | null): NormValue => {
  const m = re.exec(raw || "");
  return m ? cap(m[1]) : null;
};
export const parseSignal = enumParser(/\b(bullish|bearish|neutral)\b/i);
export const parseCallPut = enumParser(/\b(call|put)\b/i);
export const parseBuySell = enumParser(/\b(buy|sell)\b/i);

const NORMALIZERS: Record<string, (raw: string | null) => NormValue> = {
  signal: parseSignal, call_put: parseCallPut, buy_sell: parseBuySell,
  strike: parsePrice, entry_ask: parsePrice, current_price: parsePrice,
  max_loss: parsePrice, breakeven: parsePrice, max_gain: parsePrice,
  stop_loss: parsePrice, take_profit: parsePrice, expiry: parseDate,
};

export function normalize(fields: TradeFields): Record<string, { raw: string; value: NormValue }> {
  const out: Record<string, { raw: string; value: NormValue }> = {};
  for (const [k, v] of Object.entries(fields)) {
    const norm = NORMALIZERS[k];
    out[k] = { raw: v, value: norm ? norm(v) : v };
  }
  return out;
}

// Multi-leg spreads: extractSpread() pulls per-leg fields + strategy economics;
// renderSpreadCard() is the gate.
export type SpreadLeg = {
  role: "short" | "long" | null;
  buy_sell: NormValue;
  call_put: NormValue;
  strike: NormValue;
  premium: NormValue; // bid (short) / ask (long), per share
  iv: string | null;
  expiry: NormValue;
};

export type SpreadFields = {
  ticker: string;
  signal: NormValue;
  strategy: string | null;
  direction: "bullish" | "bearish" | null;
  current_price: NormValue;
  legs: SpreadLeg[];
  net: number | null; // |net premium| per share
  netKind: "credit" | "debit" | null;
  max_loss: NormValue;
  max_gain: NormValue;
  breakeven: NormValue;
  pop: string | null;
  risk_reward: string | null;
  span: { start: number; end: number } | null; // extent of structured content
};

// a line that INTRODUCES a leg: "Leg 1 (short put …)", "- Short Put 150 Bid: …".
// Must start the line — a mid-prose "…the long call butterfly…" is NOT a leg.
const LEG_HEADER_G
  = /^[ \t]*(?:[-*+][ \t]*)?\**[ \t]*\(?(?:leg\s*\d+|(?:short|long)\s+(?:\d+Δ\s+)?(?:put|call))\b[^\n]*$/gim;
// prose-leg fallbacks: "Leg 1: Sell 20.0 Call expiring May 1, 2026, premium ~$0.83"
const INLINE_LEG = /\b(sell|buy|short|long)\s+(?:the\s+)?\$?([\d,]+(?:\.\d+)?)\s+(call|put)s?\b/i;
const INLINE_PREMIUM = /\bpremium\b[^$\n]*(?:US)?\$\s*([\d,]+(?:\.\d+)?)/i;
const INLINE_EXPIRY = /\bexpir\w*[^\n]*/i;
// where economics begin (so leg parsing doesn't bleed into them)
const ECON_START
  = /^[^\n]*\b(?:strategy\s+(?:premium|economics)|net\s+(?:credit|debit)|max(?:imum)?\s+(?:loss|risk|gain)|total\s+(?:outlay|debit)|break[\s-]?even|probability of profit)\b/im;

// Compact spread format: both strikes on ONE line ("US$150 (short) / US$147 (long)")
// + per-leg premiums as Buy/Sell sub-bullets, no "Leg N" sections.
const COMPACT_STRIKE_G = /(?:US)?\$\s*([\d,]+(?:\.\d+)?)\s*\((short|long)\)/gi;
// role word BEFORE its price; [^/\n$] keeps a role tied to ITS price
const ROLE_STRIKE_G = /\b(short|sell|long|buy)\b[^/\n$]*?(?:US)?\$\s*([\d,]+(?:\.\d+)?)/gi;
// per-leg premium sub-bullet: role word starts the line, price keyword before the colon
const COMPACT_PREMIUM_G
  = /^[ \t]*[-*]?[ \t]*\**[ \t]*(short|sell|long|buy)\b[^:\n]*?\b(?:bid|ask|mid|price|premium)\b[^:\n]*:[ \t]*(?<val>[^\n]*\$[^\n]*)$/gim;
// explicit net-premium label, plus its '=' formula form
const NET_PREMIUM_RES = [LABEL_TMPL, eqTmpl(LABEL_TMPL)].flatMap(t =>
  ["(?:strategy|net)\\s+premium", "net\\s+(?:credit|debit)"]
    .map(kw => new RegExp(t.replace("{kw}", kw), "im")));
const RISK_REWARD_RES = ["risk\\s*[-/]?\\s*(?:to\\s*)?reward"]
  .map(kw => new RegExp(LABEL_TMPL.replace("{kw}", kw), "im"));

/**
 * grab() variant reporting match position. With empty inline value, falls through
 * to the label's indented continuation and uses the LAST $-bearing line. With
 * `preferDollar`, an inline value lacking a $ (a formula) is likewise upgraded.
 */
function grabAt(block: string, re: RegExp, preferDollar = false): { value: string; start: number; end: number } | null {
  const m = re.exec(block);
  if (!m) return null;
  const start = m.index ?? 0;
  let end = start + m[0].length;
  let v = clean(m.groups?.val);
  if (!v || (preferDollar && !v.includes("$"))) {
    const labelIndent = (/^[ \t]*/.exec(m[0]) as RegExpExecArray)[0].length;
    let first: { value: string; end: number } | null = null;
    let dollar: { value: string; end: number } | null = null;
    let lineStart = block.indexOf("\n", end) + 1;
    while (lineStart > 0 && lineStart < block.length) {
      const nl = block.indexOf("\n", lineStart);
      const lineEnd = nl === -1 ? block.length : nl;
      const line = block.slice(lineStart, lineEnd);
      const indent = (/^[ \t]*/.exec(line) as RegExpExecArray)[0].length;
      if (!line.trim() || indent <= labelIndent) break; // continuation block over
      if (EXAMPLE_LINE.test(line)) { // skip worked examples
        lineStart = lineEnd + 1;
        continue;
      }
      const cleaned = clean(line);
      if (cleaned) {
        if (!first) first = { value: cleaned, end: lineEnd };
        if (line.includes("$")) dollar = { value: cleaned, end: lineEnd };
      }
      lineStart = lineEnd + 1;
    }
    // a non-empty inline formula is only ever upgraded by a $ line
    const hit = v ? dollar : (dollar ?? first);
    if (!hit && !v) return null;
    if (hit) {
      v = hit.value;
      end = hit.end;
    }
  }
  return { value: v, start, end };
}

function grabFirst(text: string, res: RegExp[], preferDollar = false): { value: string; start: number; end: number } | null {
  for (const re of res) {
    const hit = grabAt(text, re, preferDollar);
    if (hit) return hit;
  }
  return null;
}

export function extractSpread(ticker: string, block: string): SpreadFields | null {
  const legMatches = Array.from(block.matchAll(LEG_HEADER_G));

  const starts: number[] = [];
  const ends: number[] = [];
  const record = (hit: { start: number; end: number } | null, offset = 0) => {
    if (hit) {
      starts.push(hit.start + offset);
      ends.push(hit.end + offset);
    }
  };

  let legs: SpreadLeg[] = [];
  if (legMatches.length >= 2) {
    const lastLeg = legMatches[legMatches.length - 1];
    const lastLegEnd = (lastLeg.index ?? 0) + lastLeg[0].length;
    const econRel = ECON_START.exec(block.slice(lastLegEnd));
    const econStart = econRel ? lastLegEnd + (econRel.index ?? 0) : block.length;

    legs = legMatches.map((lm, i) => {
      const sliceStart = lm.index ?? 0;
      const sliceEnd = i + 1 < legMatches.length ? (legMatches[i + 1].index ?? block.length) : econStart;
      const text = block.slice(sliceStart, sliceEnd);

      const bs = grabFirst(text, FIELD_LABEL_RES.buy_sell);
      const cp = grabFirst(text, FIELD_LABEL_RES.call_put);
      const st = grabFirst(text, FIELD_LABEL_RES.strike);
      const ex = grabFirst(text, FIELD_LABEL_RES.expiry);
      const pr = grabFirst(text, FIELD_LABEL_RES.entry_ask); // bid (short) / ask (long)
      const iv = grabFirst(text, FIELD_LABEL_RES.iv);
      [bs, cp, st, ex, pr, iv].forEach(h => record(h, sliceStart));

      let buySell = bs ? parseBuySell(bs.value) : null;
      let callPut = cp ? parseCallPut(cp.value) : null;
      let strike = st ? parsePrice(st.value) : null;
      let premium = pr ? parsePrice(pr.value) : null;
      let expiry = ex ? parseDate(ex.value) : null;

      // prose-leg fallbacks when the labeled grabs come up empty
      if (strike == null) {
        const m = INLINE_LEG.exec(text);
        if (m) {
          strike = parseFloat(m[2].replace(/,/g, ""));
          if (buySell == null) buySell = /^(?:sell|short)$/i.test(m[1]) ? "Sell" : "Buy";
          if (callPut == null) callPut = cap(m[3]);
          record({ start: m.index ?? 0, end: (m.index ?? 0) + m[0].length }, sliceStart);
        }
      }
      if (premium == null) {
        const m = INLINE_PREMIUM.exec(text);
        if (m) {
          premium = parseFloat(m[1].replace(/,/g, ""));
          record({ start: m.index ?? 0, end: (m.index ?? 0) + m[0].length }, sliceStart);
        }
      }
      if (expiry == null) {
        const m = INLINE_EXPIRY.exec(text);
        if (m) expiry = parseDate(m[0]);
      }

      const headerRole = /\bshort\b/i.test(lm[0]) ? "short" : /\blong\b/i.test(lm[0]) ? "long" : null;
      return {
        role: headerRole ?? (buySell === "Sell" ? "short" : buySell === "Buy" ? "long" : null),
        buy_sell: buySell,
        call_put: callPut,
        strike,
        premium,
        iv: iv ? iv.value : null,
        expiry,
      };
    });
  }

  const sig = grabFirst(block, FIELD_LABEL_RES.signal);
  const strat = grabFirst(block, FIELD_LABEL_RES.strategy);
  const cur = grabFirst(block, FIELD_LABEL_RES.current_price);
  const ml = grabFirst(block, FIELD_LABEL_RES.max_loss, true);
  const mg = grabFirst(block, FIELD_LABEL_RES.max_gain, true);
  const be = grabFirst(block, FIELD_LABEL_RES.breakeven, true);
  const pop = grabFirst(block, FIELD_LABEL_RES.pop);
  [sig, strat, cur, ml, mg, be, pop].forEach(h => record(h));

  // compact-format fill: leg sections didn't yield 2 strikes — read the
  // role-annotated strike line + Buy/Sell premium sub-bullets instead.
  if (legs.filter(l => typeof l.strike === "number").length < 2) {
    const stHit = grabFirst(block, FIELD_LABEL_RES.strike);
    const toRole = (w: string) => (/^(?:short|sell)$/i.test(w) ? "short" as const : "long" as const);
    const toStrike = (n: string) => parseFloat(n.replace(/,/g, ""));
    let pairs = stHit
      ? Array.from(stHit.value.matchAll(COMPACT_STRIKE_G)).map(m => ({ role: toRole(m[2]), strike: toStrike(m[1]) }))
      : [];
    if (stHit && !(pairs.some(p => p.role === "short") && pairs.some(p => p.role === "long"))) {
      pairs = Array.from(stHit.value.matchAll(ROLE_STRIKE_G)).map(m => ({ role: toRole(m[1]), strike: toStrike(m[2]) }));
    }
    const roles = new Set(pairs.map(p => p.role));
    if (stHit && roles.has("short") && roles.has("long")) {
      record(stHit);
      const stratCP = strat ? parseCallPut(strat.value) : null;
      const exHit = grabFirst(block, FIELD_LABEL_RES.expiry);
      if (exHit) record(exHit);

      const prem: Partial<Record<"short" | "long", { premium: NormValue; call_put: NormValue }>> = {};
      for (const m of Array.from(block.matchAll(COMPACT_PREMIUM_G))) {
        const role = /^(short|sell)$/i.test(m[1]) ? "short" as const : "long" as const;
        if (!prem[role]) {
          prem[role] = { premium: parsePrice(m.groups?.val ?? null), call_put: parseCallPut(m[0]) };
          record({ start: m.index ?? 0, end: (m.index ?? 0) + m[0].length });
        }
      }

      for (const p of pairs) {
        const role = p.role;
        let leg = legs.find(l => l.role === role);
        if (!leg) {
          leg = { role, buy_sell: null, call_put: null, strike: null, premium: null, iv: null, expiry: null };
          legs.push(leg);
        }
        if (typeof leg.strike !== "number") leg.strike = p.strike;
        if (leg.premium == null) leg.premium = prem[role]?.premium ?? null;
        if (leg.call_put == null) leg.call_put = prem[role]?.call_put ?? stratCP;
        if (leg.buy_sell == null) leg.buy_sell = role === "short" ? "Sell" : "Buy";
        if (leg.expiry == null && exHit) leg.expiry = parseDate(exHit.value);
      }
    }
  }
  if (legs.length < 2) return null;

  const signal = sig ? parseSignal(sig.value) : null;
  const strategy = strat ? strat.value.split("(")[0].trim() : null;
  const direction: "bullish" | "bearish" | null
    = signal === "Bullish" || (!!strategy && /\bbull\b/i.test(strategy))
      ? "bullish"
      : signal === "Bearish" || (!!strategy && /\bbear\b/i.test(strategy))
        ? "bearish"
        : null;

  // net premium from the legs: sold − bought (positive => credit)
  const short = legs.find(l => l.role === "short");
  const long = legs.find(l => l.role === "long");
  let net: number | null = null;
  let netKind: "credit" | "debit" | null = null;
  if (typeof short?.premium === "number" && typeof long?.premium === "number") {
    const diff = short.premium - long.premium;
    net = +Math.abs(diff).toFixed(2);
    netKind = diff >= 0 ? "credit" : "debit";
  }
  // fallback: explicit label; kind from its own keyword, else the vertical type
  if (net == null) {
    const np = grabFirst(block, NET_PREMIUM_RES, true);
    const v = np ? parseEconAmount(np.value) : null;
    if (np && typeof v === "number") {
      const line = block.slice(np.start, np.end);
      net = Math.abs(v);
      if (/\bdebit\b/i.test(line)) netKind = "debit";
      else if (/\bcredit\b/i.test(line)) netKind = "credit";
      else if (strategy && /\b(bull\s+put|bear\s+call)\b/i.test(strategy)) netKind = "credit";
      else if (strategy && /\b(bull\s+call|bear\s+put)\b/i.test(strategy)) netKind = "debit";
      record(np);
    }
  }

  // vertical economics: when unlabeled, max loss/gain follow from strike width and
  // net premium (per contract, ×100). Both are magnitudes (the LLM may sign them).
  const mag = (v: NormValue): NormValue => (typeof v === "number" ? Math.abs(v) : v);
  let maxLoss: NormValue = ml ? mag(parseEconAmount(ml.value)) : null;
  let maxGain: NormValue = mg ? mag(parseEconAmount(mg.value)) : null;
  let width: number | null = null;
  if (typeof short?.strike === "number" && typeof long?.strike === "number") {
    width = Math.abs(short.strike - long.strike);
  }
  else {
    const strikes = legs.map(l => l.strike).filter((v): v is number => typeof v === "number");
    if (strikes.length === 2) width = Math.abs(strikes[0] - strikes[1]);
  }
  // a labeled net too big to be per-share is the per-contract figure — rescale
  if (net != null && width != null && width > 0 && net >= width && net / 100 < width) {
    net = +(net / 100).toFixed(2);
  }
  if (net != null && netKind != null && width != null && width > 0 && net < width) {
    if (maxLoss == null) maxLoss = +((netKind === "credit" ? width - net : net) * 100).toFixed(2);
    if (maxGain == null) maxGain = +((netKind === "credit" ? net : width - net) * 100).toFixed(2);
  }

  // free-prose Risk/Reward line: surfaced as a footnote only when it directly
  // follows the field run (a detached mention stays in the text flow)
  let riskReward: string | null = null;
  if (ends.length) {
    const rr = grabFirst(block, RISK_REWARD_RES);
    const curEnd = Math.max(...ends);
    if (rr && rr.start >= curEnd && !block.slice(curEnd, rr.start).trim()) {
      riskReward = rr.value;
      record(rr);
    }
  }

  return {
    ticker,
    signal,
    strategy,
    direction,
    current_price: cur ? parsePrice(cur.value) : null,
    legs,
    net,
    netKind,
    max_loss: maxLoss,
    max_gain: maxGain,
    breakeven: be ? parseEconAmount(be.value) : null,
    pop: pop ? pop.value : null,
    risk_reward: riskReward,
    span: starts.length ? { start: Math.min(...starts), end: Math.max(...ends) } : null,
  };
}

export function renderSpreadCard(ticker: string, block: string): SpreadFields | null {
  if (!isSpread(block)) return null;
  const s = extractSpread(ticker, block);
  if (!s) return null;
  if (s.legs.filter(l => typeof l.strike === "number").length < 2) return null;
  // needs substance beyond two strikes — but economics may be null formulas, so
  // a named strategy or net premium also qualifies
  if (s.breakeven == null && s.max_loss == null && s.max_gain == null && s.net == null && !s.strategy) return null;
  return s;
}

export function iterBlocks(botResponse: string): Array<{ ticker: string; block: string }> {
  const ms = Array.from(botResponse.matchAll(HEADER));
  return ms.map((m, i) => {
    const start = (m.index ?? 0) + m[0].length;
    const end = i + 1 < ms.length ? (ms[i + 1].index ?? botResponse.length) : botResponse.length;
    return { ticker: m[1], block: botResponse.slice(start, end) };
  });
}

// Streaming glue: split `output` into ordered segments so the renderer streams
// text live and swaps a block to a card the moment it closes (next header appears
// or streaming finished). The still-growing last block stays `pending` until then.
export type Segment =
  | { type: "text"; text: string }
  | { type: "pending"; ticker: string; text: string }
  | {
    type: "card";
    ticker: string;
    fields: TradeFields;
    normalized: Record<string, { raw: string; value: NormValue }>;
    raw: string;
  }
  // directional idea that clears the gate but has no numeric strike
  | {
    type: "qualitative";
    ticker: string;
    fields: TradeFields;
    normalized: Record<string, { raw: string; value: NormValue }>;
    raw: string;
  }
  | { type: "spread"; ticker: string; fields: SpreadFields; raw: string };

// FIRST contiguous run of bullet/label lines (+ indented continuations), ending
// at the first blank/non-bullet line. Prose after it is re-emitted as text.
function bulletSpan(block: string): { start: number; end: number } | null {
  const lines = block.split("\n");
  let offset = 0;
  let start = -1;
  let end = 0;
  for (const line of lines) {
    // bullet = `-`/`*`/`+` FOLLOWED BY whitespace — excludes `**bold**` headings
    const isBullet = /^[ \t]*[-*+][ \t]/.test(line);
    const isContinuation = start !== -1 && /^[ \t]+\S/.test(line);
    if (start === -1) {
      if (isBullet) {
        start = offset;
        end = offset + line.length;
      }
    }
    else if (isBullet || isContinuation) {
      end = offset + line.length;
    }
    else {
      break;
    }
    offset += line.length + 1; // +1 for the consumed '\n'
  }
  return start === -1 ? null : { start, end };
}

// Field-label regexes bounding a card's structured region — every field but
// `rationale` (prose, kept as trailing text). Tested per line.
const SPAN_FIELD_RES: RegExp[] = Object.entries(FIELD_LABEL_RES)
  .filter(([field]) => field !== "rationale")
  .flatMap(([, res]) => res);

const isFieldLine = (line: string): boolean => SPAN_FIELD_RES.some(re => re.test(line));

/**
 * Span from FIRST to LAST field line. Unlike bulletSpan, BRIDGES sub-headings and
 * blank lines between field groups, so a split card covers the whole spec (instead
 * of leaking field bullets into visible text). Trailing prose stays as text.
 */
function fieldSpan(block: string): { start: number; end: number } | null {
  const lines = block.split("\n");
  let offset = 0;
  let start = -1;
  let end = -1;
  for (const line of lines) {
    const lineEnd = offset + line.length;
    if (isFieldLine(line)) {
      if (start === -1) start = offset;
      end = lineEnd;
    }
    else if (start !== -1 && end === offset - 1 && /^[ \t]+\S/.test(line)) {
      end = lineEnd; // indented value-continuation after the last field line
    }
    offset = lineEnd + 1; // +1 for the consumed '\n'
  }
  return start === -1 ? null : { start, end };
}

export function segmentMessage(output: string, streaming = false): Segment[] {
  const ms = Array.from(output.matchAll(HEADER));
  if (!ms.length) return [{ type: "text", text: output }];

  const segments: Segment[] = [];
  const firstIdx = ms[0].index ?? 0;
  if (firstIdx > 0) segments.push({ type: "text", text: output.slice(0, firstIdx) });

  // Emit [lead text] + card + [trailing text]. `span` is the card's structured
  // extent within `parseSrc`; `live` marks the trailing text as still-streaming.
  const emit = (
    span: { start: number; end: number } | null,
    parseSrc: string,
    tailSrc: string,
    ticker: string,
    mkSegment: (raw: string) => Segment,
    live: boolean,
  ) => {
    const lead = span ? parseSrc.slice(0, span.start) : "";
    if (lead.trim()) segments.push({ type: "text", text: lead });
    segments.push(mkSegment(span ? parseSrc.slice(span.start, span.end) : parseSrc));
    const trail = span ? tailSrc.slice(span.end) : "";
    if (trail.trim()) segments.push(live ? { type: "pending", ticker, text: trail } : { type: "text", text: trail });
  };

  ms.forEach((m, i) => {
    const start = m.index ?? 0;
    const end = i + 1 < ms.length ? (ms[i + 1].index ?? output.length) : output.length;
    const region = output.slice(start, end);
    const ticker = m[1];
    const isLast = i === ms.length - 1;
    const block = output.slice(start + m[0].length, end);

    // last streaming block renders on the fly: parse only fully-arrived lines
    const onTheFly = isLast && streaming;
    const lastNl = block.lastIndexOf("\n");
    const parseSrc = onTheFly ? (lastNl >= 0 ? block.slice(0, lastNl + 1) : "") : block;

    const fields = parseSrc ? renderCard(ticker, parseSrc) : null;
    if (fields) {
      const normalized = normalize(fields);
      // no real strike => directional idea, not a fillable contract: use the
      // qualitative card, not a half-empty number card
      const kind = typeof normalized.strike?.value === "number" ? "card" : "qualitative";
      const span = fieldSpan(parseSrc) ?? bulletSpan(parseSrc);
      // while streaming, surface the trailing tail only once the idea has closed
      const closed = !onTheFly || (!!span && parseSrc.slice(span.end).trim().length > 0);
      emit(span, parseSrc, closed ? block : parseSrc, ticker,
        raw => ({ type: kind, ticker, fields, normalized, raw } as Segment), onTheFly);
      return;
    }

    const spread = parseSrc ? renderSpreadCard(ticker, parseSrc) : null;
    if (spread) {
      emit(spread.span, parseSrc, block, ticker,
        raw => ({ type: "spread", ticker, fields: spread, raw }), onTheFly);
      return;
    }

    segments.push(onTheFly ? { type: "pending", ticker, text: region } : { type: "text", text: region });
  });
  return segments;
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}
